import { Copy, Edit2, MessageText1, SaveAdd, Sparkles, Trash } from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuLabel,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { AI_PRESETS, EDITOR_OVERLAY_Z_CLASSNAME } from '@/components/ui/RichTextEditor/constants';
import { APP_FONT } from '@/constants';
import type { AIImprovementIntent } from '@/api/ai';
import type { PaidEntitlementPlan } from '@/api/entitlements';
import type { ReactNode } from 'react';

type AvailablePlan = {
    id: PaidEntitlementPlan;
    name: string;
};

interface SectionContextMenuProps {
    children: ReactNode;
    /** Report is concluded: the menu degrades to "Copiar texto" only. */
    disabled: boolean;
    hasContent: boolean;
    canUseAI: boolean;
    isAIBlocked?: boolean;
    aiAvailablePlan?: AvailablePlan | null;
    canComment: boolean;
    canSaveAsDefault: boolean;
    isDefault?: boolean;
    isUpdatingDefault?: boolean;
    canClear: boolean;
    onOpenChange?: (open: boolean) => void;
    onEditHere: () => void;
    onSelectAIPreset: (intent: AIImprovementIntent) => void;
    onCopy: () => void;
    onComment: () => void;
    onSaveAsDefault: () => void;
    onClear: () => void;
}

/**
 * Right-click menu for a text section of the report canvas.
 *
 * Purely presentational, like `TableContextMenu`: every capability arrives as a
 * boolean and every action as a callback, so `EditableSection` stays the single
 * owner of behaviour. The trigger is `asChild`, which adds no DOM — the hidden
 * measurer renders a second copy of every section and must keep measuring the
 * same heights.
 */
export function SectionContextMenu({
    children,
    disabled,
    hasContent,
    canUseAI,
    isAIBlocked,
    aiAvailablePlan,
    canComment,
    canSaveAsDefault,
    isDefault,
    isUpdatingDefault,
    canClear,
    onOpenChange,
    onEditHere,
    onSelectAIPreset,
    onCopy,
    onComment,
    onSaveAsDefault,
    onClear,
}: SectionContextMenuProps) {
    const showAI = !disabled && canUseAI;
    const showCopy = hasContent;
    const showComment = !disabled && canComment;
    const showSaveAsDefault = !disabled && canSaveAsDefault;
    const showClear = !disabled && canClear && hasContent;

    // A concluded report keeps only "Copiar texto", so an empty section there
    // has nothing to offer. Hand back the plain trigger rather than opening an
    // empty popup — and let the browser's own menu through.
    if (disabled && !showCopy) {
        return <>{children}</>;
    }

    return (
        <ContextMenu onOpenChange={onOpenChange}>
            <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
            {/* Above the floating section editor (z-[70]) so right-clicking a
                different section while one is open does not tuck the menu
                behind the editor panel. */}
            <ContextMenuContent
                className={EDITOR_OVERLAY_Z_CLASSNAME}
                style={{ fontFamily: APP_FONT }}
            >
                {!disabled && (
                    <ContextMenuItem onSelect={onEditHere}>
                        <div className="flex items-center gap-2">
                            <Edit2 className="size-4 text-slate-500" />
                            Editar aqui
                        </div>
                    </ContextMenuItem>
                )}

                {showAI && (
                    <>
                        <ContextMenuSeparator />
                        <ContextMenuLabel className="flex items-center gap-2 py-1 text-xs font-medium text-slate-500">
                            <Sparkles className="size-3.5" />
                            Melhorar com IA
                            {isAIBlocked && aiAvailablePlan ? (
                                <FeatureUnavailableChip
                                    availablePlan={aiAvailablePlan}
                                    side="left"
                                    className="ml-auto"
                                />
                            ) : null}
                        </ContextMenuLabel>
                        {AI_PRESETS.map((preset) => (
                            <ContextMenuItem
                                key={preset.key}
                                onSelect={() => onSelectAIPreset(preset.key)}
                            >
                                <div className="flex items-center gap-2">
                                    <preset.icon className="size-4 text-slate-500" />
                                    {preset.label}
                                </div>
                            </ContextMenuItem>
                        ))}
                    </>
                )}

                {(showCopy || showComment || showSaveAsDefault) && !disabled && (
                    <ContextMenuSeparator />
                )}

                {showCopy && (
                    <ContextMenuItem onSelect={onCopy}>
                        <div className="flex items-center gap-2">
                            <Copy className="size-4 text-slate-500" />
                            Copiar texto
                        </div>
                    </ContextMenuItem>
                )}

                {showComment && (
                    <ContextMenuItem onSelect={onComment}>
                        <div className="flex items-center gap-2">
                            <MessageText1 className="size-4 text-slate-500" />
                            Comentar nesta seção
                        </div>
                    </ContextMenuItem>
                )}

                {showSaveAsDefault && (
                    <ContextMenuItem
                        onSelect={onSaveAsDefault}
                        disabled={isDefault || isUpdatingDefault}
                    >
                        <div className="flex items-center gap-2">
                            <SaveAdd className="size-4 text-slate-500" />
                            {isUpdatingDefault ? 'Salvando...' : 'Usar como padrão'}
                        </div>
                    </ContextMenuItem>
                )}

                {showClear && (
                    <>
                        <ContextMenuSeparator />
                        <ContextMenuItem
                            onSelect={onClear}
                            className="text-destructive focus:text-destructive"
                        >
                            <div className="flex items-center gap-2">
                                <Trash className="size-4" />
                                Limpar conteúdo
                            </div>
                        </ContextMenuItem>
                    </>
                )}
            </ContextMenuContent>
        </ContextMenu>
    );
}
